import { Button, Group } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { TextField } from '@mui/material';
import { useContext, useEffect, useReducer, useRef } from 'react';
import { flushSync } from 'react-dom';
import { textFieldStyles } from '../../muiStyles/textFieldStyles';
import DateTextField from '../DateTextField/DateTextField';
import MatterAutocomplete from '../MatterAutocomplete';
import { QuickEntryModal } from '../QuickEntryModal/QuickEntryModal';
import UserAutocomplete from '../UserAutocomplete';
import { defaultState, formReducer } from './reducer';
import { TaskData, TaskEvent } from '../../database/baseTypes';
import { AuthenticatedSessionContext } from '../../authenticatedSession/AuthenticatedSessionContext';
import { useTaskAdd } from '@/database/hooks/tasks';
import { ymdFromLocalDate } from '@/database/yearMonthDayUtil';
import { deleteDoc, DocumentReference } from 'firebase/firestore';

const TaskModal = ({
  fixedMatterId,
  onClose,
}: {
  fixedMatterId: string | undefined;
  onClose: () => void;
}) => {
  const authSession = useContext(AuthenticatedSessionContext);
  const [state, dispatch] = useReducer(
    formReducer,
    defaultState(authSession?.userIds.firestore, undefined),
  );
  const startingInputRef = useRef<any>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if ((event.target as Element)?.className === 'modal-backdrop') {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    startingInputRef.current?.focus();
  }, []);

  const addTask = useTaskAdd();

  const handleSave = async () => {
    if (!authSession) {
      return;
    }

    let hasErrors = false;
    if (!state.description.value) {
      hasErrors = true;
      dispatch({ type: 'SET_DESCRIPTION_ERROR', error: 'Required' });
    }
    if (!state.assignedTo.value) {
      hasErrors = true;
      dispatch({ type: 'SET_ASSIGNED_TO_ERROR', error: 'Required' });
    }

    if (hasErrors) {
      notifications.show({
        title: 'Missing required fields',
        message: 'Highlighted fields are required',
        color: 'red.4',
        autoClose: 6000,
      });
      return;
    }

    // Create a creation event like TaskRow.tsx does
    const creationEvent: TaskEvent = {
      type: 'created',
      timestamp: new Date(),
      userId: authSession.userIds.firestore,
      details: 'Task created',
    };

    const newTaskEntry: TaskData = {
      assignedTo: state.assignedTo.value!,
      matter: fixedMatterId ?? state.matter.value?.key ?? '',
      description: state.description.value!,
      dueYmd: state.dueDate.value
        ? ymdFromLocalDate(state.dueDate.value)
        : undefined,
      createdTimestamp: new Date(),
      isComplete: false,
      events: [creationEvent], // Add creation event
    };
    const ref = await addTask(newTaskEntry);
    const successNotificationId = `new-task-entry-${ref?.id ?? Date.now()}`;

    notifications.show({
      id: successNotificationId,
      title: 'Task created 📋',
      message: (
        <Group justify='space-between' wrap='nowrap'>
          <span>New task created successfully</span>
          <Button
            variant='white'
            color='dark'
            size='compact-xs'
            onClick={async () => {
              notifications.hide(successNotificationId);
              await handleUndo(ref);
            }}
          >
            Undo
          </Button>
        </Group>
      ),
      color: 'blue.4',
      autoClose: 2000,
    });

    // Update state synchronously so that we can focus on the starting field without a re-render getting in the way.
    flushSync(() => {
      dispatch({ type: 'CLEAR_FORM' });
    });

    // Close modal after brief delay to show success message
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const handleUndo = async (
    refToDelete: DocumentReference<TaskData> | undefined,
  ) => {
    if (!refToDelete) {
      return;
    }
    await deleteDoc(refToDelete);
    notifications.show({
      title: 'Undo successful',
      message: 'Task has been removed',
      color: 'blue.4',
      autoClose: 1500,
    });
  };

  return (
    <QuickEntryModal title='New Task' onClose={onClose} onSave={handleSave}>
      <UserAutocomplete
        firmDb={authSession?.db}
        error={!!state.assignedTo.error}
        selectedUserId={state.assignedTo.value}
        inputRef={startingInputRef}
        label='Assignee'
        onSelect={(userId) =>
          dispatch({
            type: 'SET_ASSIGNED_TO_VAL',
            assignedTo: userId ?? undefined,
          })
        }
      />
      {!fixedMatterId && (
        <MatterAutocomplete
          firmDb={authSession?.db}
          error={!!state.matter.error}
          value={state.matter.value}
          required={false}
          onSelect={(matter) => {
            dispatch({ type: 'SET_MATTER_VAL', matter: matter || undefined });
          }}
        />
      )}
      <TextField
        sx={textFieldStyles}
        label='Description'
        multiline
        rows={4}
        error={!!state.description.error}
        value={state.description.value ?? ''}
        required={true}
        onChange={(e) =>
          dispatch({ type: 'SET_DESCRIPTION_VAL', description: e.target.value })
        }
      />
      <DateTextField
        label='Due Date'
        value={state.dueDate.value ?? null}
        error={!!state.dueDate.error}
        required={false}
        onChange={(value) =>
          dispatch({ type: 'SET_DUE_DATE_VAL', dueDate: value ?? undefined })
        }
      />
    </QuickEntryModal>
  );
};

export default TaskModal;
